import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  hcpCategoryListDtoSchema,
  hcpServiceListDtoSchema,
  housecallProStatusDtoSchema,
  irrigationPartListDtoSchema,
  type HcpCategoryListDto,
  type HcpServiceListDto,
  type HousecallProStatusDto,
  type IrrigationPartListDto,
} from '@lib/dto/housecall-pro.dto'
import type { IrrigationPartKind } from '@lib/enums/irrigation-part-kind.enum'
import type {
  SetHcpWritesInput,
  UpdateHousecallProInput,
} from '@lib/input/housecall-pro.input'
import type { IHousecallProService } from '@/types/HousecallProService.types'

/** Service for the Housecall Pro connection and the part pickers it feeds. */
class HousecallProService implements IHousecallProService {
  async getStatus(): Promise<HousecallProStatusDto> {
    try {
      return await api.get('/housecall-pro', housecallProStatusDtoSchema)
    } catch (error) {
      logger.error('Failed to load the Housecall Pro connection', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async listTopCategories(): Promise<HcpCategoryListDto> {
    try {
      return await api.get(
        '/housecall-pro/categories',
        hcpCategoryListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list Housecall Pro categories', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async listServices(): Promise<HcpServiceListDto> {
    try {
      return await api.get('/housecall-pro/services', hcpServiceListDtoSchema)
    } catch (error) {
      logger.error('Failed to list Housecall Pro services', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async update(input: UpdateHousecallProInput): Promise<HousecallProStatusDto> {
    try {
      return await api.put('/housecall-pro', input, housecallProStatusDtoSchema)
    } catch (error) {
      logger.error('Failed to save the Housecall Pro settings', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setWrites(input: SetHcpWritesInput): Promise<HousecallProStatusDto> {
    try {
      return await api.put(
        '/housecall-pro/writes',
        input,
        housecallProStatusDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to switch writing to Housecall Pro', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async sync(): Promise<HousecallProStatusDto> {
    try {
      return await api.post(
        '/housecall-pro/sync',
        {},
        housecallProStatusDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to read materials from Housecall Pro', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async listParts(kind?: IrrigationPartKind): Promise<IrrigationPartListDto> {
    const query =
      kind === undefined ? '' : `?${new URLSearchParams({ kind }).toString()}`
    try {
      return await api.get(
        `/irrigation-parts${query}`,
        irrigationPartListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list irrigation parts', {
        error: toErrorMessage(error),
        kind,
      })
      throw error
    }
  }
}

export const housecallProService: IHousecallProService =
  new HousecallProService()
